/**
 * Downloadable content packs v2 (curriculum 2, docs/PERF.md "Content packs v2"). The server publishes DELTA releases of
 * per-world chunks; GET /api/v1/content/v2/manifest lists the hash of every part (shell, world chunks, locale catalogs).
 * On launch the newest cached release (IndexedDB) is used when it is newer than the bundled build; then the manifest is
 * asked for (If-None-Match) and ONLY the parts whose hash differs from what the device has are downloaded, each checked
 * with the core checks (`checkShell`, `checkWorldChunk`, `checkCatalog`) before it is cached. A newer release is announced
 * with the non-blocking "new lessons" notice; tapping it swaps it in (also used automatically on the next launch). A
 * server rollback (version <= bundled) clears the cache. Without a cache or a network the bundled content is used.
 */
import { create } from 'zustand';
import {
  CONTENT_VERSION_V2,
  checkCatalog,
  checkShell,
  checkWorldChunk,
  contentHash,
  shellHash,
  type ChunkId,
  type ContentManifestV2,
  type ContentShell,
  type Locale,
  type WorldChunk,
} from '@sketchling/core';
import { contentShell, initContent, replaceShell, setContentOverrides } from './content';
import { API_ORIGIN } from './api';

const DB = 'sketchling-content';
const STORE = 'packs';

function open(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    try {
      if (typeof indexedDB === 'undefined') return resolve(null);
      const req = indexedDB.open(DB, 1);
      req.onupgradeneeded = () => req.result.createObjectStore(STORE);
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

async function tx<T>(mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest | void): Promise<T | null> {
  const db = await open();
  if (!db) return null;
  return new Promise((resolve) => {
    const t = db.transaction(STORE, mode);
    const req = fn(t.objectStore(STORE));
    t.oncomplete = () => {
      resolve(req ? ((req.result as T | undefined) ?? null) : null);
      db.close();
    };
    t.onerror = () => {
      resolve(null);
      db.close();
    };
  });
}

const get = <T,>(key: string) => tx<T>('readonly', (s) => s.get(key));
const put = (key: string, v: unknown) => tx('readwrite', (s) => void s.put(v, key));
const clearAll = () => tx('readwrite', (s) => void s.clear());

interface CachedManifest {
  etag: string;
  manifest: ContentManifestV2;
  savedAt: string;
}
/** Parts are stored content-addressed: `shell:<hash>`, `chunk:<id>:<hash>`, `cat:<locale>:<part>:<hash>`. */
const shellKey = (h: string) => `shell:${h}`;
const chunkKey = (id: string, h: string) => `chunk:${id}:${h}`;
const catKey = (l: string, part: string, h: string) => `cat:${l}:${part}:${h}`;

export interface ContentUpdate {
  version: number;
  lessons: number;
}

interface ContentState {
  /** Version currently in use (bundled or downloaded). */
  version: number;
  source: 'bundled' | 'cache' | 'server';
  /** Newer release downloaded and cached, waiting for the user (non-blocking notice). */
  pending: ContentUpdate | null;
  /** Bumped when content is swapped in (remounts the screens). */
  rev: number;
  /** Parts downloaded by the last update (tests / cheats). */
  lastDownload: string[];
}

export const useContentState = create<ContentState>(() => ({ version: CONTENT_VERSION_V2, source: 'bundled', pending: null, rev: 0, lastDownload: [] }));

/** Overrides that read a release's parts from the cache (hash-checked). */
function overridesFor(m: ContentManifestV2, shell: ContentShell) {
  return {
    shell,
    chunk: async (id: ChunkId, hash: string) => {
      if (!m.chunks[id] || m.chunks[id] !== hash) return null;
      const c = await get<WorldChunk>(chunkKey(id, hash));
      return c && c.hash === hash ? c : null;
    },
    catalog: async (locale: Locale, part: string) => {
      const h = m.locales[locale]?.[part];
      return h ? await get<Record<string, string>>(catKey(locale, part, h)) : null;
    },
  };
}

/** Before the first render: use the cached release when it is complete, valid and newer than the bundled build. */
export async function loadCachedContent(timeoutMs = 600): Promise<void> {
  const read = (async () => {
    const cm = await get<CachedManifest>('manifest');
    if (!cm || cm.manifest.version <= CONTENT_VERSION_V2) return null;
    const shell = await get<ContentShell>(shellKey(cm.manifest.shell));
    return shell ? { cm, shell } : null;
  })();
  const got = await Promise.race([read, new Promise<null>((r) => setTimeout(() => r(null), timeoutMs))]);
  if (!got) return;
  if (checkShell(got.shell).length) {
    void clearAll();
    return;
  }
  setContentOverrides(overridesFor(got.cm.manifest, got.shell));
  useContentState.setState({ version: got.cm.manifest.version, source: 'cache' });
}

let checking = false;
let pendingApply: { manifest: ContentManifestV2; shell: ContentShell } | null = null;

async function fetchJson(path: string): Promise<unknown> {
  const res = await fetch(`${API_ORIGIN}/api/v1/content/v2/${path}`);
  if (!res.ok) throw new Error(`${path}: ${res.status}`);
  return res.json();
}

/** Bundled manifest (hashes of the parts shipped with the app). */
const bundledManifest = async (): Promise<ContentManifestV2 | null> => {
  const files = import.meta.glob<string>('../generated/content/manifest.json', { query: '?url', import: 'default', eager: true });
  const url = files['../generated/content/manifest.json'];
  return url ? ((await (await fetch(url)).json()) as ContentManifestV2) : null;
};

/**
 * Ask the server for a newer release (on launch; non-blocking). Downloads only the parts whose hash differs from the
 * bundled build and from what is already cached. Returns what happened, for tests / cheats.
 */
export async function checkForContentUpdate(): Promise<'none' | 'notModified' | 'update' | 'rollback' | 'rejected' | 'offline'> {
  if (checking) return 'none';
  checking = true;
  try {
    await initContent();
    const cached = await get<CachedManifest>('manifest');
    const headers: Record<string, string> = {};
    if (cached?.etag) headers['If-None-Match'] = cached.etag;
    let res: Response;
    try {
      res = await fetch(`${API_ORIGIN}/api/v1/content/v2/manifest`, { headers, cache: 'no-store' });
    } catch {
      return 'offline';
    }
    if (res.status === 304) return 'notModified';
    if (!res.ok) return 'offline';
    const etag = res.headers.get('ETag') ?? '';
    const m = (await res.json().catch(() => null)) as ContentManifestV2 | null;
    if (!m || typeof m.version !== 'number' || !m.chunks || typeof m.shell !== 'string') return 'rejected';
    if (m.version <= CONTENT_VERSION_V2) {
      // The server serves the bundled content (or rolled back): forget any downloaded release.
      if (cached) await clearAll();
      return useContentState.getState().version > CONTENT_VERSION_V2 ? 'rollback' : 'none';
    }
    if (m.version === useContentState.getState().version && !pendingApply) {
      await put('manifest', { etag, manifest: m, savedAt: new Date().toISOString() });
      return 'none';
    }
    const base = await bundledManifest();
    const have = async (key: string) => !!(await get(key));
    const downloaded: string[] = [];
    // Shell
    let shell = await get<ContentShell>(shellKey(m.shell));
    if (!shell) {
      const s = (await fetchJson(`shell?h=${m.shell}`)) as ContentShell;
      if (checkShell(s).length || shellHash(s) !== m.shell) return 'rejected';
      await put(shellKey(m.shell), s);
      shell = s;
      downloaded.push('shell');
    }
    // World chunks whose hash differs from the bundled one (bundled ones are read from the app itself).
    for (const [id, h] of Object.entries(m.chunks) as [ChunkId, string][]) {
      if (base?.chunks[id] === h || (await have(chunkKey(id, h)))) continue;
      const c = await fetchJson(`chunk/${id}?h=${h}`);
      const errors = checkWorldChunk(c, shell, { chunk: id, hash: h });
      if (errors.length) {
        console.warn('[content] chunk rejected', id, errors.slice(0, 3));
        return 'rejected';
      }
      await put(chunkKey(id, h), c);
      downloaded.push(id);
    }
    for (const [loc, parts] of Object.entries(m.locales ?? {})) {
      for (const [part, h] of Object.entries(parts ?? {})) {
        if (base?.locales[loc as Locale]?.[part] === h || (await have(catKey(loc, part, h)))) continue;
        const cat = await fetchJson(`locale/${loc}/${part}?h=${h}`);
        if (!checkCatalog(cat) || contentHash(cat) !== h) return 'rejected';
        await put(catKey(loc, part, h), cat);
        downloaded.push(`${loc}/${part}`);
      }
    }
    await put('manifest', { etag, manifest: m, savedAt: new Date().toISOString() });
    pendingApply = { manifest: m, shell };
    const cur = contentShell();
    const known = new Set(cur?.pack.miniUnits.map((x) => x.id) ?? []);
    const lessons = shell.pack.miniUnits.filter((x) => !known.has(x.id)).length;
    useContentState.setState({ pending: { version: m.version, lessons }, lastDownload: downloaded });
    return 'update';
  } catch (e) {
    console.warn('[content] update failed', e);
    return 'offline';
  } finally {
    checking = false;
  }
}

/** The user tapped the notice: swap the downloaded release in now. */
export async function applyPendingContent(): Promise<void> {
  const p = pendingApply;
  if (!p) return;
  pendingApply = null;
  setContentOverrides(overridesFor(p.manifest, p.shell));
  await replaceShell(p.shell);
  useContentState.setState((s) => ({ version: p.manifest.version, source: 'server', pending: null, rev: s.rev + 1 }));
}

export function dismissContentUpdate(): void {
  useContentState.setState({ pending: null });
}
